import React, { useMemo, useState } from 'react';
import { getTeamLogoUrl } from '../utils/helpers';
import { logos } from '../teams/logos';
import { listOfTeams } from '../teams/team';
import { MoveRight, MoveLeft } from 'lucide-react';

function erf(x) {
  // Abramowitz & Stegun approximation
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741,
        a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);
  const t = 1.0 / (1.0 + p * x);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return sign * y;
}

function normalCdf(z) {
  return 0.5 * (1 + erf(z / Math.SQRT2));
}

/** Home win probability from ratings. Neutral-site games use HFA = 0. */
function homeWinProb(homeRating, awayRating, homeFieldAdvantage = 2, sigma = 17) {
  const margin = homeRating - awayRating + homeFieldAdvantage;
  return normalCdf(margin / sigma);
}

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric' });

const s = (str) =>
  String(str)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '-');

const formatTime = (dateStr) =>
  new Date(dateStr).toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

const TeamLogo = ({ teamName, isWinner, isManual }) => {
  const logoUrl =
    getTeamLogoUrl(listOfTeams[teamName], !isWinner) ??
    '/projection/fcs.png';

  return (
    <div className="teamLogo">
      <img src={logoUrl} alt={`${teamName} logo`} />
    </div>
  );
};

const Schedule = ({ games = [], change, teams = [] }) => {
  const [CURRENT_WEEK, setWeek] = useState(4);
  const [conferenceFilter, setConferenceFilter] = useState('');
  const [sort, setSort] = useState('x');

  // FIX 1: Added CURRENT_WEEK to dependencies so filteredGames updates on week change
  const filteredGames = useMemo(() => {
    let lastDate = '';

    return games
.filter((game) => {
  const is2026 = new Date(game.startDate).getFullYear() === 2026;

  // Week 0 condition for 2026
  if (CURRENT_WEEK === 0) {
   // return game.week === 0 && is2026;
  }

  // Regular season games (Weeks 1 to 13)
  if (CURRENT_WEEK < 14) {
    return game.week === CURRENT_WEEK && game.type !== 'championship';
  }

  if (CURRENT_WEEK == 15) {
    return game.week === 14 && game.type !== 'championship';
  }

  // Championship games (Week 14)
  return CURRENT_WEEK === 14 && game.type === 'championship';
})
.filter((game) => logos[game.homeTeam] && logos[game.awayTeam])
        .sort((a, b) =>  sort == "x" ? ((b.homeAvg+32.5)*(b.awayAvg+30)-(a.homeAvg+32.5)*(a.awayAvg+30)) : null)
      .map((game) => {
        
          const dateFormatted = formatDate(game.startDate);
        const showDateHeader = dateFormatted !== lastDate;
        if (showDateHeader) lastDate = dateFormatted;


        const ou = 50 + Math.random()*5;

        const homeScore = ((ou / 2) + (game.homeAvg - game.awayAvg + 2.5) / 2).toFixed(1);
        const awayScore = ((ou / 2) + (game.awayAvg - game.homeAvg - 2.5) / 2).toFixed(1);
        //console.log(homeScore);
        //console.log(awayScore);

        if(!game.completed){
        //game.awayPoints = awayScore;
        //game.homePoints = homeScore;
        }

        return {
          ...game,
          dateFormatted,
          timeFormatted: formatTime(game.startDate),
          showDateHeader,
          isBigMatchup: (game.homeAvg || 0) + (game.awayAvg || 0) > 320,
        };
      });
  }, [games, CURRENT_WEEK, sort]); 

  const flipped = [
    'Wake Forest',
    'Texas State',
    'UCLA',
    'North Dakota State',
    'UL Monroe',
    'UTEP',
  ];

  const conferences = [
        ...Array.from(new Set(teams.map(t => t.conference))).sort((a, b) => a.localeCompare(b))
    ];

  return (
    <div className="schedule">
      <div className="list-filters">
        
        <select
                    id="conference-select"
                    value={conferenceFilter}
                    onChange={(e) => setConferenceFilter(e.target.value)}
                >
                                        <option key={"FBC"} value={""}>Filter by Conference</option>

                    {conferences.map((conf, index) => (
                        <option key={index} value={conf}>{conf}</option>
                    ))}
                </select>

                <select
                    id="sort-select"
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                >
                                        <option key={"ex"} value={"x"}>Sort By Excitement</option>
                                        <option key={"da"} value={"d"}>Sort By Date</option>

                </select>
        
      </div>

      <h1>
         <div className="list-filters">
         <div
          className="slim-butt"
          onClick={() => setWeek((prev) => Math.max(0, prev - 1))}
        >
         {CURRENT_WEEK > 1 && <><MoveLeft size={12} /> &nbsp;Week {CURRENT_WEEK - 1}</>}
        </div>
         <span>Week {CURRENT_WEEK}</span>
         <div
          className="slim-butt"
          onClick={() => setWeek((prev) => Math.min(15, prev + 1))}
        >
          {CURRENT_WEEK < 15 && <>Week {CURRENT_WEEK + 1}&nbsp;<MoveRight size={12} /></>}
        </div></div>
         </h1>

      <div>
        {filteredGames.map((game) => {
          const { id, homeTeam, awayTeam, winner, manual, isBigMatchup } = game;

          const rawT1 = teams.find((t) => t.team === homeTeam);
          const rawT2 = teams.find((t) => t.team === awayTeam);


          if(conferenceFilter != "" && rawT1.conference !== conferenceFilter && rawT2.conference !== conferenceFilter){
              return;
          }

          // FIX 2: Safely calculate team colors without mutating team props directly
          const homeColor = flipped.includes(homeTeam)
            ? rawT1?.alternateColor ?? "#d5ac2a"
            : rawT1?.color ?? "#d5ac2a";
          const awayColor = flipped.includes(awayTeam)
            ? rawT2?.alternateColor ?? "#d5ac2a"
            : rawT2?.color ?? "#d5ac2a";

          const containerClasses = [
            'matchup-container',
            isBigMatchup ? 'featured' : '',
            manual ? 'manual' : '',
            game.completed ? 'completed' : '',
          ]
            .filter(Boolean)
            .join(' ');


            const odds = Math.round(homeWinProb(rawT1?.avg, rawT2?.avg)*100, 0);

          return (
            <div className="row" key={id}>
              {isBigMatchup && <h2>Big Matchup</h2>}

              <div className={containerClasses}>
                <div className="teams">
                  {/* Home Team */}
                  <div
                    className={`schedule-team home team ${s(homeTeam)} ${
                      winner === homeTeam ? 'winner' : ''
                    }`}
                    onClick={() => game.completed ? null : change(id, 'home', false)}
                  >
                    <TeamLogo
                      teamName={homeTeam}
                      isWinner={winner === homeTeam}
                      isManual={manual}
                    />
                    <p className="sub">
                      {homeTeam}
                      
                    </p>
                  </div>
                  {homeColor && (
                    <style>{`
                      .winner.${s(homeTeam)} .teamLogo {
                        background: linear-gradient(90deg, 
                            color-mix(in srgb, ${homeColor} 55%, white) 0%, 
                            color-mix(in srgb, ${homeColor} 85%, black) 18%, 
                            color-mix(in srgb, ${homeColor} 35%, white) 38%, 
                            color-mix(in srgb, ${homeColor} 90%, black) 58%, 
                            color-mix(in srgb, ${homeColor} 70%, black) 78%, 
                            color-mix(in srgb, ${homeColor} 45%, white) 100%
                        );
                        background-size: 300% 600%;
                        border-color: ${homeColor};
                      }
                   
                    `}</style>
                  )}

                  <div className="info">

                  {game.completed || (game.awayPoints > 1 || game.homePoints > 1) ? game.awayPoints + " - " + game.homePoints : <>{!game.startDate ? "Date TBD" : formatDate(game.startDate)}<br/>{!game.startDate || formatTime(game.startDate) == "12:00 AM" ? "Time TBD" : formatTime(game.startDate)}</>}
                  </div>
                  {/* Away Team */}
                  <div
                    className={`schedule-team away team ${s(awayTeam)} ${
                      winner === awayTeam ? 'winner' : ''
                    }`}
                    onClick={() => game.completed ? null : change(id, 'away', false)}
                  >
                    <TeamLogo
                      teamName={awayTeam}
                      isWinner={winner === awayTeam}
                      isManual={manual}
                    />
                    <p className="sub">
                      {awayTeam}
                     
                    </p>
                  </div>
                  {awayColor && (
                    <style>{`
                       .winner.${s(awayTeam)} .teamLogo {
                        background: linear-gradient(90deg, 
                            color-mix(in srgb, ${awayColor} 55%, white) 0%, 
                            color-mix(in srgb, ${awayColor} 85%, black) 18%, 
                            color-mix(in srgb, ${awayColor} 35%, white) 38%, 
                            color-mix(in srgb, ${awayColor} 90%, black) 58%, 
                            color-mix(in srgb, ${awayColor} 70%, black) 78%, 
                            color-mix(in srgb, ${awayColor} 45%, white) 100%
                        );
                        background-size: 300% 600%;
                        border-color: ${awayColor};
                      }
                    `}</style>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <h1>
         <div className="list-filters">
         <div
          className="slim-butt"
          onClick={() => setWeek((prev) => Math.max(0, prev - 1))}
        >
         {CURRENT_WEEK > 0 && <><MoveLeft size={12} /> &nbsp;Week {CURRENT_WEEK}</>}
        </div>
         <span>Week {CURRENT_WEEK}</span>
         <div
          className="slim-butt"
          onClick={() => setWeek((prev) => Math.min(15, prev + 1))}
        >
          {CURRENT_WEEK < 15 && <>Week {CURRENT_WEEK + 1}&nbsp;<MoveRight size={12} /></>}
        </div></div>
         </h1>
    </div>
  );
};

export default Schedule;